/**
 * Fetch wrapper for the PyDialog JSON API.
 *
 * - Always sends credentials (session cookie).
 * - On non-ok responses, throws ApiError with the `detail` message.
 * - Does NOT auto-redirect on 401 — callers (AuthContext, ProtectedRoute)
 *   handle auth state and navigation themselves.
 */

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

export async function api<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // A 401 on a non-auth endpoint means the session was invalidated (logged in
    // elsewhere, or expired). Signal the app to drop auth state and redirect.
    if (res.status === 401 && !path.startsWith("/api/auth/")) {
      window.dispatchEvent(new Event("auth:expired"));
    }
    throw new ApiError(
      body.detail || res.statusText,
      res.status
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

// Convenience methods

export function apiGet<T>(path: string): Promise<T> {
  return api<T>(path, { method: "GET" });
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, {
    method: "POST",
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, {
    method: "PUT",
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(path: string): Promise<T> {
  return api<T>(path, { method: "DELETE" });
}
